import React from 'react';

/**
 * The §4.11 "house style" insight surface — a translucent, blurred, tinted glass
 * panel with a hairline border in the same hue. Generalised out of
 * `route-intelligence/AnalysisAlert.js` (which was feature-local, fetched its own
 * data, and whose findings could not drill). Here the content is entirely props.
 *
 * Superset contract — serves BOTH V6 builds so the second-to-merge reconcile is
 * trivial:
 *   - `tone`        one of `gray | amber | emerald | red` (the TONES keys). `gray`
 *                   is the calm brand-blue wash, not an error colour.
 *   - `eyebrow`     a small uppercase label ("Key Findings"); null for the verdict.
 *   - `title`       an optional heading rendered above the body.
 *   - `children`    free content (the Conversation build's verdict/body).
 *   - `findings`    `[{ icon, tone, text, onClick }]` — one line each (the Search
 *                   Console build's Key Findings). A line with `onClick` is a button
 *                   that drills; without one it is static text. `findings` and
 *                   `children` may both be present; findings render after children.
 *   - `dismissible` / `onDismiss`  show a close control (the verdict never dismisses,
 *                   so it passes neither).
 *
 * The per-line `onClick` is the part AnalysisAlert does not have and the reason this
 * is a build, not an import.
 */

// Glass tints per tone. Kept local (not TONES.bg, which is an opaque gradient) so
// the surface reads as translucent glass over the page. Every colour is defined in
// the base class and only the dark variant is swapped, so both themes are covered.
const GLASS = {
  gray: {
    surface: 'bg-blue-50/50 dark:bg-blue-500/10 border-blue-200/60 dark:border-blue-400/20',
    icon: 'text-blue-500 dark:text-blue-400',
  },
  amber: {
    surface: 'bg-amber-50/50 dark:bg-amber-500/10 border-amber-200/60 dark:border-amber-400/20',
    icon: 'text-amber-500 dark:text-amber-400',
  },
  emerald: {
    surface: 'bg-emerald-50/50 dark:bg-emerald-500/10 border-emerald-200/60 dark:border-emerald-400/20',
    icon: 'text-emerald-500 dark:text-emerald-400',
  },
  red: {
    surface: 'bg-red-50/50 dark:bg-red-500/10 border-red-200/60 dark:border-red-400/20',
    icon: 'text-red-500 dark:text-red-400',
  },
};

const toneClasses = (tone) => GLASS[tone] || GLASS.gray;

// Icon glyphs keyed by name (inline SVG path children). Lifted from AnalysisAlert's
// set and extended. A name with no glyph falls back to a small dot, so an unknown
// icon never crashes a line.
const ICON_PATHS = {
  opportunity: <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2v1.1h5v-1.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3Z M9 21h6" />,
  discovery: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polygon points="15.5 8.5 13.5 13.5 8.5 15.5 10.5 10.5" />
    </>
  ),
  alert: (
    <>
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  trend: <path d="M3 17l6-6 4 4 8-8M21 7v6M21 7h-6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.35-4.35" />
    </>
  ),
};

const FindingIcon = ({ name, className }) => (
  <svg
    className={`w-5 h-5 shrink-0 mt-0.5 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICON_PATHS[name] || <circle cx="12" cy="12" r="3" />}
  </svg>
);

const CloseIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const InsightSurface = ({
  tone = 'gray',
  eyebrow = null,
  eyebrowClassName = 'text-gray-500 dark:text-gray-400',
  title = null,
  children = null,
  findings = null,
  dismissible = false,
  onDismiss = null,
  // Portal "MY WORK" (Epic 2 reconcile): the verdict is the loudest text on the
  // page (Part C). `variant='verdict'` adds more padding and renders children
  // inside a `<p class="gi-verdict">` (the --type-verdict token, index.css:460),
  // keeping the glass tint from GLASS[tone]. Default `'panel'` is the current
  // behaviour — the Conversation/Search-Console callers pass no `variant`, so
  // they are unaffected. No top-level `onClick`: the spec says the verdict "is
  // the synthesis" and has no click; add it only if a later epic needs it.
  variant = 'panel',
}) => {
  const t = toneClasses(tone);
  const isVerdict = variant === 'verdict';
  const padding = isVerdict ? 'p-5 md:p-7' : 'p-4 md:p-5';
  const showDismiss = Boolean(dismissible && onDismiss);
  const list = Array.isArray(findings) ? findings : [];
  const bodySpacing = eyebrow || title ? 'mt-2' : '';

  return (
    <section
      data-testid="insight-surface"
      data-tone={tone}
      data-variant={variant}
      className={`relative rounded-xl border backdrop-blur-sm shadow-sm transition-colors ${padding} ${t.surface}`}
    >
      {showDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="absolute top-2 right-2 p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/40 dark:hover:bg-gray-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
        >
          <CloseIcon />
        </button>
      )}

      {eyebrow && (
        <div className={`text-[13px] uppercase tracking-wide font-semibold ${eyebrowClassName} ${showDismiss ? 'pr-8' : ''}`}>
          {eyebrow}
        </div>
      )}

      {title && (
        <h2 className={`text-lg font-semibold text-gray-900 dark:text-gray-100 ${eyebrow ? 'mt-1' : ''} ${showDismiss && !eyebrow ? 'pr-8' : ''}`}>
          {title}
        </h2>
      )}

      {children && (isVerdict ? (
        <p className={`gi-verdict text-gray-900 dark:text-gray-100 ${bodySpacing}`}>{children}</p>
      ) : (
        <div className={bodySpacing}>{children}</div>
      ))}

      {list.length > 0 && (
        <ul className={`space-y-0.5 ${eyebrow || title || children ? 'mt-2' : ''}`}>
          {list.map((f, idx) => {
            const iconColor = toneClasses(f.tone).icon;
            const body = (
              <>
                <FindingIcon name={f.icon} className={iconColor} />
                <span className="text-sm leading-snug text-gray-800 dark:text-gray-100 text-left transition-colors group-hover:text-[#32B1FF] dark:group-hover:text-[#32B1FF]">{f.text}</span>
              </>
            );
            if (typeof f.onClick === 'function') {
              return (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={f.onClick}
                    data-finding={idx}
                    className="group w-full flex items-start gap-2.5 rounded-lg px-2 py-1 -mx-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]"
                  >
                    {body}
                  </button>
                </li>
              );
            }
            return (
              <li key={idx} className="flex items-start gap-2.5 px-2 py-1 -mx-2" data-finding={idx}>
                {body}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default InsightSurface;
